import { createApi, fetchBaseQuery, retry } from "@reduxjs/toolkit/query/react";
import getBaseUrl from "../getBaseUrl";

const maxRetries = 2;

const baseQuery = retry(fetchBaseQuery(), { maxRetries });

const REACT_APP_LIFT_NET_LOCAL_API_OFFLINE = process.env.REACT_APP_LIFT_NET_LOCAL_API_OFFLINE || "";

export const monitoringAPI = createApi({
  reducerPath: "monitoringAPI",
  baseQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["tagGetOrganizationCascader", "tagGetFavoriteGrids"],
  refetchOnMountOrArgChange: true,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    //* *********************************************************** */
    //* GET ALL ORGANIZATIONS AND ITS CASCADER DATA */
    getOrganizationsCascader: builder.query({
      query: (body: any) => ({
        url: `${getBaseUrl()}/monitor-view/`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetOrganizationCascader"],
    }),
    //* *********************************************************** */
    //* GET FAVORITE GRIDS */
    getFavoriteGrids: builder.query({
      query: (body: any) => ({
        url: `${getBaseUrl()}/monitor-grid/?&page=1&limit=9999`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetFavoriteGrids"],
    }),
    //* *********************************************************** */
    //* GET FAVORITE GRID BY ID */
    getFavoriteGrid: builder.query({
      query: (body: any) => ({
        url: `${getBaseUrl()}/monitor-grid/${body.idGrid}`,
        headers: { authorization: body.token },
      }),
      providesTags: [],
    }),
    //* *********************************************************** */
    //* GET FAVORITE GRIDS BY USER */
    getFavoriteGridsByUser: builder.query({
      query: (body: any) => ({
        url: `${getBaseUrl()}/monitor-grid/by-user/${body.idUser}`,
        headers: { authorization: body.token },
      }),
      providesTags: ["tagGetFavoriteGrids"],
    }),
    //* *********************************************************** */
    //* GET FAVORITE GRIDS BY USER */
    getFavoriteGridsByOrganization: builder.query({
      query: (body: any) => ({
        url: `${getBaseUrl()}/monitor-grid/by-organization/${body.idOrganization}`,
        headers: { authorization: body.token },
      }),
      providesTags: [],
    }),
    //* *********************************************************** */
    //* POST FAVORITE GRIDS */
    postFavoriteGrids: builder.mutation({
      query: (body: any) => ({
        url: `${getBaseUrl()}/monitor-grid/`,
        method: "post",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetFavoriteGrids"],
    }),
    //* *********************************************************** */
    //* PUT FAVORITE GRIDS */
    putFavoriteGrids: builder.mutation({
      query: (body: any) => ({
        url: `${getBaseUrl()}/monitor-grid/${body.id}`,
        method: "put",
        headers: { authorization: body.token },
        body: body.body,
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* DELETE FAVORITE GRIDS */
    deleteFavoriteGrids: builder.mutation({
      query: (body: any) => ({
        url: `${getBaseUrl()}/monitor-grid/${body.id}`,
        method: "delete",
        headers: { authorization: body.token },
      }),
      invalidatesTags: ["tagGetFavoriteGrids"],
    }),
    //* *********************************************************** */
    //* POST SEND COMMANDS */
    postMonitoringCommand: builder.mutation({
      query: (body: any) => ({
        url: `${
          process.env.REACT_APP_EDGE_SERVER_CONNECTION === "OFFLINE"
            ? REACT_APP_LIFT_NET_LOCAL_API_OFFLINE
            : getBaseUrl()
        }${
          process.env.REACT_APP_EDGE_SERVER_CONNECTION === "OFFLINE"
            ? "/monitoring/command"
            : "/monitor-grid/command"
        }${process.env.REACT_APP_EDGE_SERVER_CONNECTION === "OFFLINE" ? "" : `/${body.serverId}`}`,
        method: "post",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* POST REFRESH ALL OFFLINE */
    postMonitoringCommandRefreshAllOffline: builder.mutation({
      query: (body: any) => ({
        url: `${REACT_APP_LIFT_NET_LOCAL_API_OFFLINE}/monitoring/refresh-command/${body.serverId}`,
        method: "post",
        body: body.body,
        headers: { authorization: body.token },
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* POST EMULATOR COMMAND */
    postEmulatorCommand: builder.mutation({
      query: (body: any) => ({
        url: `${process.env.REACT_APP_WEB_SOCKET_EMULATOR}/monitor-grid/command`,
        method: "post",
        body: body.body,
      }),
      invalidatesTags: [],
    }),
    //* *********************************************************** */
    //* GET BANK MESSAGES */
    getBankMessages: builder.query({
      query: (body: any) => ({
        url: `${getBaseUrl()}/banks/messages/${body.bankId}`,
        headers: { authorization: body.token },
      }),
      providesTags: [],
    }),
    //* *********************************************************** */
  }),
});

export const {
  useLazyGetOrganizationsCascaderQuery,
  useLazyGetFavoriteGridsQuery,
  useLazyGetFavoriteGridQuery,
  useLazyGetFavoriteGridsByUserQuery,
  useLazyGetFavoriteGridsByOrganizationQuery,
  usePostFavoriteGridsMutation,
  usePutFavoriteGridsMutation,
  useDeleteFavoriteGridsMutation,
  usePostMonitoringCommandMutation,
  usePostMonitoringCommandRefreshAllOfflineMutation,
  usePostEmulatorCommandMutation,
  useLazyGetBankMessagesQuery,
} = monitoringAPI;
